import React, { useEffect, useState } from 'react';
import { consumeNavPath } from '@/lib/adminNav';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Trash2,
  Plus,
  Edit,
  Percent,
  GitMerge,
  ListChecks,
  PlusCircle,
  Link2,
} from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { useAppContext } from '@/contexts/AppContext';
import type { PaymentType } from '@/contexts/AppContext';
import PayoutColumnMappingManager from './PayoutColumnMappingManager';
import SettingsSidebarLayout from './ui/settings-sidebar-layout';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const toNum = (v: string): number | null => {
  if (v === '' || v === null || v === undefined) return null;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

const fmtPct = (v: number | null | undefined): string =>
  v === null || v === undefined ? '—' : `${v}%`;

interface PendingMerge {
  sourceId: string;
  targetId: string;
  sourceName: string;
  targetName: string;
}

type PtTab = 'current' | 'new' | 'mapping';

const PaymentTypeManagement: React.FC = () => {
  const {
    paymentTypes,
    addPaymentType,
    removePaymentType,
    updatePaymentType,
    mergePaymentTypes,
  } = useAppContext();

  const [activeTab, setActiveTab] = useState<PtTab>('current');

  // Deep-link from the Site Map (Settings -> Site Map) -- see adminNav.ts.
  // path[0] ("payment-types") is consumed by AdminDashboard.tsx for
  // settingsSubTab; this reads path[1].
  useEffect(() => {
    const path = consumeNavPath();
    if (path?.[1]) setActiveTab(path[1] as PtTab);
  }, []);

  // New payment type form
  const [newPaymentType, setNewPaymentType] = useState('');
  const [hasMaximum, setHasMaximum] = useState(false);
  const [maximumAmount, setMaximumAmount] = useState('');
  const [baseCommission, setBaseCommission] = useState('');
  const [contractorRate, setContractorRate] = useState('');
  const [leasedRate, setLeasedRate] = useState('');
  const [isPerUnit, setIsPerUnit] = useState(false);
  const [perUnitAmount, setPerUnitAmount] = useState('');
  const [saving, setSaving] = useState(false);

  // Inline edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editHasMaximum, setEditHasMaximum] = useState(false);
  const [editMaximumAmount, setEditMaximumAmount] = useState('');
  const [editBaseCommission, setEditBaseCommission] = useState('');
  const [editContractorRate, setEditContractorRate] = useState('');
  const [editLeasedRate, setEditLeasedRate] = useState('');
  const [editIsPerUnit, setEditIsPerUnit] = useState(false);
  const [editPerUnitAmount, setEditPerUnitAmount] = useState('');

  // Merge confirmation (raised when a rename collides with an existing name)
  const [pendingMerge, setPendingMerge] = useState<PendingMerge | null>(null);
  const [merging, setMerging] = useState(false);

  const resetNewForm = () => {
    setNewPaymentType('');
    setHasMaximum(false);
    setMaximumAmount('');
    setBaseCommission('');
    setContractorRate('');
    setLeasedRate('');
    setIsPerUnit(false);
    setPerUnitAmount('');
  };

  const handleAddPaymentType = async () => {
    if (!newPaymentType.trim()) {
      toast({ title: 'Error', description: 'Please enter a payment type name' });
      return;
    }

    if (
      paymentTypes.some(
        (type) =>
          type.name.trim().toLowerCase() === newPaymentType.trim().toLowerCase()
      )
    ) {
      toast({ title: 'Error', description: 'Payment type already exists' });
      return;
    }

    if (hasMaximum && (!maximumAmount || parseFloat(maximumAmount) <= 0)) {
      toast({ title: 'Error', description: 'Please enter a valid maximum amount' });
      return;
    }

    if (isPerUnit && (!perUnitAmount || parseFloat(perUnitAmount) <= 0)) {
      toast({
        title: 'Error',
        description: 'Please enter a valid per-unit amount (e.g. value per hoist)',
      });
      return;
    }

    const newType: Omit<PaymentType, 'id'> = {
      name: newPaymentType.trim(),
      hasMaximum,
      maximumAmount: hasMaximum ? parseFloat(maximumAmount) : undefined,
      baseCommissionPercent: toNum(baseCommission),
      contractorPercent: toNum(contractorRate),
      leasedPercent: toNum(leasedRate),
      isPerUnit,
      perUnitAmount: isPerUnit ? parseFloat(perUnitAmount) : null,
    };

    setSaving(true);
    try {
      await addPaymentType(newType);
      resetNewForm();
      toast({
        title: 'Success',
        description: `Payment type "${newType.name}" added`,
      });
      setActiveTab('current');
    } catch {
      /* error toast handled in context */
    } finally {
      setSaving(false);
    }
  };

  const handleRemovePaymentType = async (id: string) => {
    const type = paymentTypes.find((t) => t.id === id);
    try {
      await removePaymentType(id);
      toast({ title: 'Success', description: `Payment type "${type?.name}" removed` });
    } catch {
      /* handled */
    }
  };

  const startEdit = (type: PaymentType) => {
    setEditingId(type.id);
    setEditName(type.name);
    setEditHasMaximum(type.hasMaximum);
    setEditMaximumAmount(type.maximumAmount?.toString() || '');
    setEditBaseCommission(
      type.baseCommissionPercent === null || type.baseCommissionPercent === undefined
        ? ''
        : String(type.baseCommissionPercent)
    );
    setEditContractorRate(
      type.contractorPercent === null || type.contractorPercent === undefined
        ? ''
        : String(type.contractorPercent)
    );
    setEditLeasedRate(
      type.leasedPercent === null || type.leasedPercent === undefined
        ? ''
        : String(type.leasedPercent)
    );
    setEditIsPerUnit(!!type.isPerUnit);
    setEditPerUnitAmount(
      type.perUnitAmount === null || type.perUnitAmount === undefined
        ? ''
        : String(type.perUnitAmount)
    );
  };

  const saveEdit = async () => {
    if (!editName.trim()) {
      toast({ title: 'Error', description: 'Please enter a payment type name' });
      return;
    }

    if (editHasMaximum && (!editMaximumAmount || parseFloat(editMaximumAmount) <= 0)) {
      toast({ title: 'Error', description: 'Please enter a valid maximum amount' });
      return;
    }

    try {
      await updatePaymentType(editingId!, {
        name: editName.trim(),
        hasMaximum: editHasMaximum,
        maximumAmount: editHasMaximum ? parseFloat(editMaximumAmount) : undefined,
        baseCommissionPercent: toNum(editBaseCommission),
        contractorPercent: toNum(editContractorRate),
        leasedPercent: toNum(editLeasedRate),
        isPerUnit: editIsPerUnit,
        perUnitAmount: editIsPerUnit ? parseFloat(editPerUnitAmount) || 0 : null,
      });
      setEditingId(null);
      toast({ title: 'Success', description: 'Payment type updated' });
    } catch (err) {
      const e = err as { code?: string; existingId?: string };
      if (e?.code === 'DUPLICATE_NAME' && e.existingId && editingId) {
        const source = paymentTypes.find((t) => t.id === editingId);
        const target = paymentTypes.find((t) => t.id === e.existingId);
        setPendingMerge({
          sourceId: editingId,
          targetId: e.existingId,
          sourceName: source?.name || 'this payment type',
          targetName: target?.name || editName.trim(),
        });
      }
    }
  };

  const confirmMerge = async () => {
    if (!pendingMerge) return;
    setMerging(true);
    try {
      await mergePaymentTypes(pendingMerge.sourceId, pendingMerge.targetId);
      toast({
        title: 'Merged',
        description: `"${pendingMerge.sourceName}" merged into "${pendingMerge.targetName}". All driver modals and payout reports now use the same payment type.`,
      });
      setPendingMerge(null);
      setEditingId(null);
    } catch {
      /* handled in context */
    } finally {
      setMerging(false);
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const tabs: { value: PtTab; label: string; description: string; icon: typeof ListChecks; count?: number }[] = [
    {
      value: 'current',
      label: 'Current Payment Types',
      description: 'View, edit & merge existing types',
      icon: ListChecks,
      count: paymentTypes.length,
    },
    {
      value: 'new',
      label: 'New Payment Type',
      description: 'Add a type with Standard / Contractor / Leased %',
      icon: PlusCircle,
    },
    {
      value: 'mapping',
      label: 'Payment Type Mapping',
      description: 'Map payout columns to payment types',
      icon: Link2,
    },
  ];

  return (
    <Card className="shadow-lg">
      <CardHeader>
        <CardTitle className="text-xl text-purple-800 flex items-center gap-2">
          <Percent className="w-5 h-5" />
          Payment Type Management
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <SettingsSidebarLayout
          tabs={tabs}
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as PtTab)}
        >
            {/* ----- Current Payment Types ----- */}
            {activeTab === 'current' && (
              <div>
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-800">
                    Current Payment Types
                  </h3>
                  <Button
                    size="sm"
                    onClick={() => setActiveTab('new')}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    New
                  </Button>
                </div>
                <div className="max-h-[560px] overflow-auto rounded-lg border p-2 space-y-2">
                  {paymentTypes.length === 0 ? (
                    <p className="text-gray-500 text-sm p-3">
                      No payment types configured
                    </p>
                  ) : (
                    paymentTypes.map((type) => (
                      <div
                        key={type.id}
                        className="flex items-start justify-between p-3 border rounded-lg gap-2 bg-white"
                      >
                        {editingId === type.id ? (
                          <div className="flex-1 space-y-2">
                            <Input
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              placeholder="Payment type name"
                            />
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id={`edit-max-${type.id}`}
                                checked={editHasMaximum}
                                onCheckedChange={(checked) =>
                                  setEditHasMaximum(checked as boolean)
                                }
                              />
                              <Label htmlFor={`edit-max-${type.id}`}>Has Maximum</Label>
                            </div>
                            {editHasMaximum && (
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={editMaximumAmount}
                                onChange={(e) => setEditMaximumAmount(e.target.value)}
                                placeholder="Maximum amount"
                              />
                            )}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                              <div>
                                <Label className="text-xs">Standard %</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  max="100"
                                  value={editBaseCommission}
                                  onChange={(e) => setEditBaseCommission(e.target.value)}
                                  placeholder="Standard %"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Contractor %</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  max="100"
                                  value={editContractorRate}
                                  onChange={(e) => setEditContractorRate(e.target.value)}
                                  placeholder="Contractor %"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Leased %</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  max="100"
                                  value={editLeasedRate}
                                  onChange={(e) => setEditLeasedRate(e.target.value)}
                                  placeholder="Leased %"
                                />
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id={`edit-perunit-${type.id}`}
                                checked={editIsPerUnit}
                                onCheckedChange={(c) => setEditIsPerUnit(c as boolean)}
                              />
                              <Label htmlFor={`edit-perunit-${type.id}`} className="text-xs">
                                Charged per unit (e.g. per hoist)
                              </Label>
                            </div>
                            {editIsPerUnit && (
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={editPerUnitAmount}
                                onChange={(e) => setEditPerUnitAmount(e.target.value)}
                                placeholder="Amount per unit ($)"
                              />
                            )}
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={saveEdit}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                Save
                              </Button>
                              <Button size="sm" variant="outline" onClick={cancelEdit}>
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge variant="secondary">{type.name}</Badge>
                                {type.hasMaximum && (
                                  <Badge variant="outline" className="text-xs">
                                    Max: ${type.maximumAmount?.toFixed(2)}
                                  </Badge>
                                )}
                                {type.isPerUnit && (
                                  <Badge variant="outline" className="text-xs">
                                    Per unit: ${Number(type.perUnitAmount || 0).toFixed(2)}
                                  </Badge>
                                )}
                              </div>
                              <div className="grid grid-cols-3 gap-2 mt-2 max-w-md">
                                <div className="rounded-md bg-gray-50 border px-2 py-1 text-center">
                                  <div className="text-[10px] uppercase text-gray-500">Standard</div>
                                  <div className="font-semibold text-gray-800 text-sm">
                                    {fmtPct(type.baseCommissionPercent)}
                                  </div>
                                </div>
                                <div className="rounded-md bg-gray-50 border px-2 py-1 text-center">
                                  <div className="text-[10px] uppercase text-gray-500">Contractor</div>
                                  <div className="font-semibold text-gray-800 text-sm">
                                    {fmtPct(type.contractorPercent)}
                                  </div>
                                </div>
                                <div className="rounded-md bg-gray-50 border px-2 py-1 text-center">
                                  <div className="text-[10px] uppercase text-gray-500">Leased</div>
                                  <div className="font-semibold text-gray-800 text-sm">
                                    {fmtPct(type.leasedPercent)}
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <button
                                onClick={() => startEdit(type)}
                                className="text-blue-500 hover:text-blue-700 p-1"
                                title="Edit"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleRemovePaymentType(type.id)}
                                className="text-red-500 hover:text-red-700 p-1"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* ----- New Payment Type ----- */}
            {activeTab === 'new' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800">New Payment Type</h3>
                <div className="space-y-4 rounded-lg border p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Payment Type Name</Label>
                      <Input
                        value={newPaymentType}
                        onChange={(e) => setNewPaymentType(e.target.value)}
                        placeholder="Enter payment type name"
                      />
                    </div>
                    <div className="flex items-end">
                      <div className="flex items-center space-x-2 pb-2">
                        <Checkbox
                          id="hasMaximum"
                          checked={hasMaximum}
                          onCheckedChange={(checked) => setHasMaximum(checked as boolean)}
                        />
                        <Label htmlFor="hasMaximum">Set Maximum Allowable Amount</Label>
                      </div>
                    </div>
                  </div>

                  {hasMaximum && (
                    <div className="max-w-xs">
                      <Label>Maximum Amount ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={maximumAmount}
                        onChange={(e) => setMaximumAmount(e.target.value)}
                        placeholder="Enter maximum amount"
                      />
                    </div>
                  )}

                  {/* Rates grid — Standard / Contractor / Leased side by side */}
                  <div className="pt-2">
                    <div className="flex items-center gap-2 mb-3 text-sm font-medium text-purple-700">
                      <Percent className="w-4 h-4" />
                      Base Commission / Driver-Payable Rates
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div className="rounded-lg border bg-purple-50/50 p-3">
                        <Label className="text-xs font-semibold text-purple-800">Standard %</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={baseCommission}
                          onChange={(e) => setBaseCommission(e.target.value)}
                          placeholder="e.g. 50"
                          className="mt-1 text-lg font-semibold text-center"
                        />
                      </div>
                      <div className="rounded-lg border bg-blue-50/50 p-3">
                        <Label className="text-xs font-semibold text-blue-800">Contractor %</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={contractorRate}
                          onChange={(e) => setContractorRate(e.target.value)}
                          placeholder="e.g. 90"
                          className="mt-1 text-lg font-semibold text-center"
                        />
                      </div>
                      <div className="rounded-lg border bg-green-50/50 p-3">
                        <Label className="text-xs font-semibold text-green-800">Leased Vehicle %</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={leasedRate}
                          onChange={(e) => setLeasedRate(e.target.value)}
                          placeholder="e.g. 100"
                          className="mt-1 text-lg font-semibold text-center"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      These are the company default rates. Individual drivers can still be
                      given an override on their profile.
                    </p>
                  </div>

                  {/* Per-unit (e.g. per-hoist) charging */}
                  <div className="pt-2 border-t">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="isPerUnit"
                        checked={isPerUnit}
                        onCheckedChange={(checked) => setIsPerUnit(checked as boolean)}
                      />
                      <Label htmlFor="isPerUnit">
                        Charged per unit (fixed increment, e.g. per hoist)
                      </Label>
                    </div>
                    {isPerUnit && (
                      <div className="mt-2 max-w-xs">
                        <Label className="text-xs">Amount per unit ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={perUnitAmount}
                          onChange={(e) => setPerUnitAmount(e.target.value)}
                          placeholder="e.g. 11.50 per hoist"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Drivers enter a quantity only — the total always goes up in this
                          set increment (e.g. 2 × ${perUnitAmount || '11.50'} = $
                          {(2 * (parseFloat(perUnitAmount) || 11.5)).toFixed(2)}).
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={resetNewForm}>
                      Clear
                    </Button>
                    <Button
                      onClick={handleAddPaymentType}
                      disabled={saving}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      {saving ? 'Adding…' : 'Add Payment Type'}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* ----- Payment Type Mapping ----- */}
            {activeTab === 'mapping' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800">Payment Type Mapping</h3>
                <PayoutColumnMappingManager />
              </div>
            )}

            <div className="bg-blue-50 p-3 rounded-lg mt-4">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Payment types are stored centrally — they appear
                automatically in the driver Payment screens. The Standard / Contractor /
                Leased percentages set the company default commission for each payment
                type, while individual drivers can be given an override on their profile.
              </p>
            </div>
        </SettingsSidebarLayout>

        <AlertDialog
          open={!!pendingMerge}
          onOpenChange={(open) => {
            if (!open && !merging) setPendingMerge(null);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <GitMerge className="w-5 h-5 text-purple-600" />
                Merge payment types?
              </AlertDialogTitle>
              <AlertDialogDescription>
                A payment type named{' '}
                <strong>"{pendingMerge?.targetName}"</strong> already exists.
                Renaming <strong>"{pendingMerge?.sourceName}"</strong> would
                create a duplicate.
                <br />
                <br />
                Merge <strong>"{pendingMerge?.sourceName}"</strong> into{' '}
                <strong>"{pendingMerge?.targetName}"</strong> instead? This keeps a
                single, consistent payment type used by the driver Payment Modal and
                all payout reports. Any per-driver commission overrides will be moved
                across, then the duplicate is removed. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={merging}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  confirmMerge();
                }}
                disabled={merging}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {merging ? 'Merging…' : 'Merge & keep consistent'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
};

export default PaymentTypeManagement;
